import { useEffect, useRef, useCallback } from 'react';
import { useSettingsStore } from '@store/settingsStore';
import { useAuthStore } from '@store/authStore';

/**
 * Hook to monitor user activity and auto-logout after inactivity
 * Shows a friendly "Are you still there?" modal instead of hard logout.
 *
 * Simplified to avoid token refresh on visibility change which can cause
 * page freezing issues. Token refresh is handled by API calls themselves.
 */
export function useIdleTimeout() {
  const sessionTimeout = useSettingsStore((state) => state.sessionTimeout);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isShowingModalRef = useRef<boolean>(false);

  /**
   * Show the session timeout modal
   */
  const showTimeoutModal = useCallback(() => {
    // Prevent showing multiple times
    if (isShowingModalRef.current) return;
    isShowingModalRef.current = true;

    // Clear the timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Show the modal
    const { setShowSessionTimeout } = useAuthStore.getState();
    setShowSessionTimeout(true);
  }, []);

  /**
   * Reset the idle timer - call this after successful re-auth
   */
  const resetTimer = useCallback(() => {
    // Reset the modal flag so we can show it again later
    isShowingModalRef.current = false;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const timeoutMs = sessionTimeout * 60 * 1000; // Convert minutes to ms

    // Update last activity time
    lastActivityRef.current = Date.now();

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      showTimeoutModal();
    }, timeoutMs);
  }, [sessionTimeout, showTimeoutModal]);

  /**
   * Handle visibility change (tab hidden/visible)
   * When the tab becomes visible again, check if session expired
   * Does NOT attempt token refresh to avoid page freezing
   */
  const handleVisibilityChange = useCallback(() => {
    // Only handle when becoming visible
    if (document.visibilityState !== 'visible') {
      // Tab is being hidden - record the time
      lastActivityRef.current = Date.now();
      return;
    }

    // If modal is already showing, don't do anything
    if (isShowingModalRef.current) return;

    const timeoutMs = sessionTimeout * 60 * 1000;
    const timeSinceActivity = Date.now() - lastActivityRef.current;

    // If the tab was hidden longer than the session timeout, show modal
    if (timeSinceActivity >= timeoutMs) {
      showTimeoutModal();
      return;
    }

    // Tab is now visible and session hasn't expired
    // Just update activity time and reset timer
    // Token refresh will happen naturally on next API call
    lastActivityRef.current = Date.now();
    resetTimer();
  }, [sessionTimeout, showTimeoutModal, resetTimer]);

  useEffect(() => {
    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity updates to reduce overhead
    let lastThrottleTime = 0;
    const handleActivity = () => {
      // If modal is showing, don't update activity
      if (isShowingModalRef.current) return;

      const now = Date.now();
      // Only update if more than 30 seconds since last update
      if (now - lastThrottleTime > 30000) {
        lastThrottleTime = now;
        lastActivityRef.current = now;
        resetTimer();
      }
    };

    // Add activity listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Handle visibility change (tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start the initial timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer, handleVisibilityChange]);

  return { resetTimer };
}
